import { revalidateLogic, useForm } from '@tanstack/react-form'
import { tareaSchema, DEFAULT_TAREA_VALUES, type TareaFormValues } from './tareaSchema'

export interface UseTareaFormOptions {
  defaultValues?: Partial<TareaFormValues>
  onSubmit?: (data: TareaFormValues) => Promise<void> | void
}

/**
 * Hook para manejar el formulario de Tarea con TanStack Form + Zod
 */
export function useTareaForm(options: UseTareaFormOptions = {}) {
  const { defaultValues, onSubmit } = options

  const form = useForm({
    defaultValues: {
      ...DEFAULT_TAREA_VALUES,
      ...defaultValues,
    } as TareaFormValues,
    // Valida al enviar y, después del primer intento, en cada cambio, así un error entre campos se limpia al corregir el otro campo.
    validationLogic: revalidateLogic(),
    validators: {
      onDynamic: ({ value }) => {
        const result = tareaSchema.safeParse(value)
        if (!result.success) {
          return result.error.issues.map((issue) => ({
            message: issue.message,
            path: issue.path,
          }))
        }
        return undefined
      },
    },
    onSubmit: async ({ value }) => {
      if (onSubmit) {
        await onSubmit(value as TareaFormValues)
      }
    },
  })

  return { form }
}

/**
 * Helper para extraer mensaje de error de TanStack Form
 * Compatible con Zod v4 (StandardSchemaV1Issue)
 */
export function getFieldError(errors: unknown[]): string | undefined {
  const first = errors[0]
  if (!first) return undefined
  if (typeof first === 'string') return first
  if (typeof first === 'object' && first !== null && 'message' in first) {
    return (first as { message: string }).message
  }
  return undefined
}

// Tipos exportados para uso en componentes
export type UseTareaFormReturn = ReturnType<typeof useTareaForm>
export type TareaForm = UseTareaFormReturn['form']
